import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection, ScheduleType } from 'webapp/enums/enums';
import type { SearchProps } from 'webapp/utils/filter';
import { isVerified } from 'webapp/utils/verification';

import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import { RECENTLY_DELETED_ID, type FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type Reminder from 'webapp/models-custom/reminder';
import type Report from 'webapp/models-custom/report';
import type ReportScheduleSetting from 'webapp/models-custom/report-schedule-setting';

export const viewSettingsNamePrefix = 'reportsViewSettings';
export const settingsNamePrefix = 'reportsFilterSettings'; // used to save filter preferences

export enum ReportCategory {
	ALL = 0,
	FAVORITE = 5,
	VERIFIED = 7,
	DELETED = RECENTLY_DELETED_ID,
}

export enum ReportFilterId {
	TAGS = 1,
	OWNER = 2,
	DATA_SOURCE = 3,
	SCHEDULE = 4,
	REMINDER = 5,
	CATEGORY = 6,
}

export default class FilterSortReport extends FilterSort<Report> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	@tracked override sortProp = 'order';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.ASC;

	override searchProps: SearchProps = {
		title: 1,
	};

	override settingsNamePrefix = settingsNamePrefix;
	override trackingObjectName = 'report';

	constructor(options: FilterSortSettings<Report>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();

		// Data source (used only)
		// Currently commented out because we would need to go through every fragment on report
		// and return it to the report (expensive operation)
		/* this.createFilter<string>({
			id: ReportFilterId.DATA_SOURCE,
			label: 'Data source',
			name: 'data source',
			selected: selectedFilters[ReportFilterId.DATA_SOURCE] as string | undefined,
			stringValues: true,
			getOptions: () => {
				return this.datasourceOptions;
			},
			filterFn: (item, selected) => {
				return item.sourceTypeKeys.includes(selected);
			},
		}); */

		// Reminder type
		this.createFilter<number>({
			id: ReportFilterId.REMINDER,
			label: 'Reminder',
			name: 'reminder',
			selected: selectedFilters[ReportFilterId.REMINDER] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.scheduleOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((selectedValue) =>
					item.reminders.some((r: Reminder) => r.scheduleType === selectedValue),
				);
			},
		});

		// Schedule type
		this.createFilter<number>({
			id: ReportFilterId.SCHEDULE,
			label: 'Schedule',
			name: 'schedule',
			selected: selectedFilters[ReportFilterId.SCHEDULE] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.scheduleOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((selectedValue) =>
					item.scheduleSettings.some(
						(ss: ReportScheduleSetting) => ss.scheduleType === selectedValue,
					),
				);
			},
		});

		// Owner
		this.createFilter<number>({
			id: ReportFilterId.OWNER,
			label: 'Owned by',
			name: 'owner',
			selected: selectedFilters[ReportFilterId.OWNER] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.ownerOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedValue) => {
					if (selectedValue === -1) {
						// exception: all others
						return !item?.isMine;
					} else {
						return item?.ownerId === selectedValue;
					}
				});
			},
		});

		// Category
		this.createFilter<number>({
			id: ReportFilterId.CATEGORY,
			label: 'Show',
			name: 'category',
			selected: selectedFilters[ReportFilterId.CATEGORY] as number[] | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.categoryOptions;
			},
			onSelectedChange: (filter) => {
				if (filter.selected.includes(ReportCategory.DELETED)) {
					void this.mmReportsService.requestDeletedReports();
				}
			},
			filterFn: function (item, selected) {
				return selected.some((category) => {
					switch (category) {
						case ReportCategory.ALL:
							return !item.deleted;
						case ReportCategory.FAVORITE:
							return item.favorite;
						case ReportCategory.VERIFIED:
							return isVerified(item.verificationInfo);
						case ReportCategory.DELETED:
							return !!item.deleted && item.canCurrentUserEditWithoutDeleted;
						default:
							return false;
					}
				});
			},
		});

		// to apply "Recently deleted" filter after page refresh when filter is set
		if (this.findFilter(ReportFilterId.CATEGORY)?.selected.includes(ReportCategory.DELETED)) {
			void this.mmReportsService.requestDeletedReports();
		}

		// Tags (used only)
		this.createFilter<number>({
			id: ReportFilterId.TAGS,
			label: 'Tags',
			name: 'tag',
			selected: selectedFilters[ReportFilterId.TAGS] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.tagOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedValue) => item.tags.includes(selectedValue));
			},
		});
	}

	override get hideViewSelector(): boolean {
		// to prevent hiding view selectors if any of view have zero items (especially for transfered account or public templates)
		return false;
	}

	override get viewOptions(): FilterOption<string>[] {
		return [
			{ id: 'table', text: 'List view', icon: 'List' },
			{ id: 'grid', text: 'Grid view', icon: 'Grid' },
		];
	}

	get categoryOptions(): FilterOption<number>[] {
		// id of filters shouldn't be changed (only add or remove) because they are saved in usres local storage
		// id [1,2,3,4,6] have already been used, but later removed so they shouldn't be used again
		const categoryOptionsBase: FilterOption<number>[] = [
			{ id: ReportCategory.ALL, text: 'All' },
			{
				id: ReportCategory.FAVORITE,
				text: 'Favorites',
				icon: 'StarFill',
				color: 'var(--databox-yellow-500)',
			},
		];

		categoryOptionsBase.push({
			id: ReportCategory.VERIFIED,
			text: 'Verified',
			icon: 'VerifiedFill',
		});

		categoryOptionsBase.push({
			id: ReportCategory.DELETED,
			text: 'Recently deleted',
			icon: 'Trash',
			class: 'top-divider',
			notCombinable: true,
		});

		return categoryOptionsBase;
	}

	get tagOptions(): FilterOption<number>[] {
		const values = this.mmReportsService.assignedTags,
			arrayCopy = Array.from(values) as FilterOption<number>[];
		if (arrayCopy.length) {
			arrayCopy.unshift({ id: 0, text: 'All' });
		}
		return arrayCopy;
	}

	get ownerOptions(): FilterOption<number>[] {
		const allItemCountByOwner: Record<number, number> = {};
		this.mmReportsService.savedRecords.forEach((item) => {
			const ownerId = item.ownerId!; // without an owner, counted under the "null" key
			if (!allItemCountByOwner[ownerId]) {
				allItemCountByOwner[ownerId] = 0;
			}
			allItemCountByOwner[ownerId]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByOwner);
	}

	/*
	@cached
	get datasourceOptions(): FilterOption<string>[] {
		// get how many boards each source is used on
		const sourceTypeArray: string[] = [];

		this.allItems.forEach((report) => {
			const sourcesFromReport = report.sourceTypeKeys;
			uniq(sourcesFromReport).forEach((key: string) => {
				sourceTypeArray.push(key);
			});
		});

		const list = this.mmSourceTypesService.getSourceTypesListWithCount(sourceTypeArray);

		list.unshift({ id: 'all', text: 'All' });

		return list;
	} */

	get scheduleOptions(): FilterOption<number>[] {
		const list: FilterOption<number>[] = [
			{ id: 0, text: 'All' },
			{ id: ScheduleType.DAILY, text: 'Daily' },
			{ id: ScheduleType.WEEKLY, text: 'Weekly' },
			{ id: ScheduleType.MONTHLY, text: 'Monthly' },
			{ id: ScheduleType.QUARTERLY, text: 'Quarterly' },
		];

		return list;
	}
}
